import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { workforceApi } from '../../finance/api/workforce';
import toast from 'react-hot-toast';
import { FiHome, FiCheck, FiX, FiRefreshCw, FiClock, FiAlertCircle } from 'react-icons/fi';
import ConfirmModal from '../shared/ConfirmModal';
import { fmtDate } from '../../utils/istDate';

export default function WfhApprovals() {
  const queryClient = useQueryClient();
  const [remarks, setRemarks] = useState({});
  const [confirm, setConfirm] = useState({ open: false, req: null, status: null });

  const { data: requests, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['pendingWfhRequests'],
    queryFn: workforceApi.getPendingWfhRequests,
  });

  const approveRejectMut = useMutation({
    mutationFn: ({ id, status, rm }) => workforceApi.approveRejectWfhRequest(id, status, rm),
    onSuccess: (data, variables) => {
      const req = variables.req;
      const empName = req?.employeeName || req?.username || (req?.userId ? `User #${req.userId}` : 'Employee');
      if (variables.status === 'APPROVED') {
        toast.success(`WFH request for ${empName} has been approved.`);
      } else {
        toast.success(`WFH request for ${empName} has been rejected.`);
      }
      setConfirm({ open: false, req: null, status: null });
      queryClient.invalidateQueries({ queryKey: ['pendingWfhRequests'] });
    },
    onError: (err) => {
      toast.error(err.message || 'Action failed');
    }
  });

  const openConfirmModal = (req, status) => {
    setConfirm({ open: true, req, status });
  };

  const handleConfirmAction = () => {
    if (!confirm.req || !confirm.status) return;
    const { req, status } = confirm;
    approveRejectMut.mutate({ id: req.id, status, rm: remarks[req.id] || '', req });
  };

  const handleRemarksChange = (id, val) => {
    setRemarks(prev => ({ ...prev, [id]: val }));
  };

  return (
    <div className="animate-fade-in space-y-4">
      <ConfirmModal
        isOpen={confirm.open}
        onClose={() => setConfirm({ open: false, req: null, status: null })}
        onConfirm={handleConfirmAction}
        loading={approveRejectMut.isLoading}
        title={confirm.status === 'APPROVED' ? 'Approve WFH Request?' : 'Reject WFH Request?'}
        message={confirm.status === 'APPROVED'
          ? `Are you sure you want to approve the WFH request for ${confirm.req?.employeeName || confirm.req?.username || 'this employee'}?`
          : `Are you sure you want to reject the WFH request for ${confirm.req?.employeeName || confirm.req?.username || 'this employee'}?`}
        confirmText={confirm.status === 'APPROVED' ? 'Approve' : 'Reject'}
        variant={confirm.status === 'APPROVED' ? 'info' : 'danger'}
      />

      <div className="flex justify-end">
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium rounded-xl text-sm transition-colors shadow-sm"
        >
          <FiRefreshCw className={isFetching ? 'animate-spin text-blue-600' : ''} /> Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">
          <FiRefreshCw className="animate-spin text-blue-600 text-2xl mx-auto mb-2" />
          <p className="text-sm font-medium">Loading pending WFH requests...</p>
        </div>
      ) : isError ? (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-center text-rose-600 flex items-center justify-center gap-2">
          <FiAlertCircle className="text-xl" />
          <span className="font-medium text-sm">Error loading requests. Ensure you have required permissions.</span>
        </div>
      ) : (!Array.isArray(requests) || requests.length === 0) ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500 max-w-md mx-auto">
          <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-3 text-emerald-600 border border-emerald-100">
            <FiHome className="text-2xl" />
          </div>
          <h3 className="text-base font-bold text-slate-800 mb-1">No Pending WFH Requests</h3>
          <p className="text-xs text-slate-500">There are currently no work-from-home requests awaiting review.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden divide-y divide-slate-100">
          {requests?.map(req => {
            const empName = req.employeeName || req.username || `User #${req.userId}`;

            return (
              <div key={req.id} className="p-5 hover:bg-slate-50/60 transition-all flex flex-col md:flex-row md:items-center justify-between gap-4">
                {/* Info */}
                <div className="space-y-2 flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 font-bold text-xs flex items-center justify-center border border-indigo-200">
                        {empName.slice(0, 2).toUpperCase()}
                      </div>
                      <span className="font-bold text-slate-800 text-base">{empName}</span>
                    </div>

                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-1.5"></span>
                      Pending
                    </span>

                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-50 text-purple-700 border border-purple-200">
                      WFH
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-slate-600">
                    <div>
                      <span className="text-slate-400 font-medium mr-1">Duration:</span>
                      <span className="font-semibold text-slate-700">{fmtDate(req.startDate)} → {fmtDate(req.endDate)}</span>
                    </div>
                  </div>

                  {req.reason && (
                    <p className="text-xs text-slate-500 line-clamp-2 max-w-3xl bg-slate-50 p-2 rounded-lg border border-slate-100">
                      <strong className="text-slate-600">Reason:</strong> {req.reason}
                    </p>
                  )}

                  <div className="max-w-md pt-1">
                    <input
                      type="text"
                      placeholder="Add optional remark..."
                      value={remarks[req.id] || ''}
                      onChange={(e) => handleRemarksChange(req.id, e.target.value)}
                      className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2.5 shrink-0 pt-2 md:pt-0 border-t md:border-t-0 border-slate-100">
                  <button
                    onClick={() => openConfirmModal(req, 'APPROVED')}
                    disabled={approveRejectMut.isLoading}
                    className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm shadow-emerald-600/20 disabled:opacity-50"
                  >
                    <FiCheck className="text-sm" /> Approve
                  </button>
                  <button
                    onClick={() => openConfirmModal(req, 'REJECTED')}
                    disabled={approveRejectMut.isLoading}
                    className="flex items-center gap-1.5 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm shadow-rose-600/20 disabled:opacity-50"
                  >
                    <FiX className="text-sm" /> Reject
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
